import { useState, useEffect, useMemo } from 'react'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import localeData from 'dayjs/plugin/localeData'
import updateLocale from 'dayjs/plugin/updateLocale'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import useFilterCollection from 'photo-module/filters-bar/core/filters.hooks'
import { useUserClubMembership } from 'spypoint/src/user-module/club-membership/core/useUserClubMembership'
import useSelectedCamera from 'camera-module/camera/core/useSelectedCamera'
import useLanguage from 'shared-module/components/language/useLanguage'
import { getLocaleFormats, capitalizeFirstLetter } from '../utils/dateFormatUtils'

// Import necessary locales
import 'dayjs/locale/en'
import 'dayjs/locale/fr'
import 'dayjs/locale/de'
import 'dayjs/locale/es'

// Enable dayjs plugins
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(localeData)
dayjs.extend(updateLocale)
dayjs.extend(localizedFormat)
dayjs.extend(relativeTime)
dayjs.extend(updateLocale)

/**
 * Custom hook for date range selection functionality
 * @returns {Object} Date range methods and state
 */
const useSpyPointDateRange = () => {
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [currentMonth, setCurrentMonth] = useState(dayjs())
  const [selectedMonth, setSelectedMonth] = useState(capitalizeFirstLetter(dayjs().format('MMMM YYYY')))

  const { addSelectedFilter, deleteSelectedFilter, selectedFilters } = useFilterCollection()
  const userMembership = useUserClubMembership()
  const selectedCamera = useSelectedCamera()
  const language = useLanguage()

  // Set the dayjs locale based on user's language
  useEffect(() => {
    dayjs.locale(language)

    // Update the selected month text when language changes
    if (currentMonth) {
      setSelectedMonth(capitalizeFirstLetter(currentMonth.locale(language).format('MMMM YYYY')))
    }
  }, [language, currentMonth])

  // Calculate date constraints based on user subscription and camera activation
  const dateConstraints = useMemo(() => {
    const today = dayjs().startOf('day')
    const maxDate = today

    // Default: 1 month history for non-Insiders Club members
    let minDate = today.subtract(1, 'month')

    // Insiders Club members get up to 1 year of history
    if (userMembership && userMembership.isMember) {
      minDate = today.subtract(1, 'year')
    }

    // If camera activation date is more recent than subscription-based date, use that
    if (selectedCamera && selectedCamera.activationDate) {
      const cameraActivationDate = dayjs(selectedCamera.activationDate).startOf('day')
      if (cameraActivationDate.isAfter(minDate)) {
        minDate = cameraActivationDate
      }
    }

    // Set a sensible default if we somehow have an invalid range
    if (!minDate.isValid() || minDate.isAfter(maxDate)) {
      minDate = maxDate.subtract(7, 'days')
    }

    return { minDate, maxDate }
  }, [userMembership, selectedCamera])

  // Load any existing date range filter when the component mounts
  useEffect(() => {
    const dateRangeFilter = selectedFilters.find(filter => filter.startsWith('dateRange:'))
    if (dateRangeFilter) {
      const [start, end] = dateRangeFilter.split(':')[1].split(' - ')

      // Convert the ISO date strings to dayjs objects
      // If the date contains time information, only take the date part
      const startDateFormatted = start ? dayjs(start.trim().split('T')[0]) : null
      const endDateFormatted = end ? dayjs(end.trim().split('T')[0]) : null

      setStartDate(startDateFormatted)
      setEndDate(endDateFormatted)
    } else if (selectedFilters.length === 0) {
      // This handles the "Clear All" case when all filters are cleared
      setStartDate(null)
      setEndDate(null)
    }
  }, [selectedFilters])

  // Set initial month display based on constraints
  useEffect(() => {
    if (startDate) {
      // If a date is already selected, show that month
      const month = startDate.clone().startOf('month')
      setCurrentMonth(month)
      setSelectedMonth(capitalizeFirstLetter(month.locale(language).format('MMMM YYYY')))
    }
  }, [dateConstraints.minDate, startDate, language])

  /**
   * Initialize the calendar view
   * @param {boolean} isOpen - Whether the calendar is open
   */
  const initializeCalendarView = (isOpen) => {
    if (isOpen && !startDate) {
      // When opening the date picker and no dates are selected yet,
      // show the current month (not the earliest available - that might be confusing)
      const month = dayjs().startOf('month')
      setCurrentMonth(month)
      setSelectedMonth(capitalizeFirstLetter(month.locale(language).format('MMMM YYYY')))
    }
  }

  /**
   * Handle date selections
   * @param {Object} date - The dayjs date object that was clicked
   */
  const handleDateClick = (date) => {
    // Validate that the date is within allowed range before setting it
    if (date.isBefore(dateConstraints.minDate) || date.isAfter(dateConstraints.maxDate)) {
      return
    }

    if (!startDate || (startDate && endDate)) {
      // Start a new selection
      setStartDate(date)
      setEndDate(null)
    } else {
      if (date.isBefore(startDate)) {
        setEndDate(startDate)
        setStartDate(date)
      } else {
        setEndDate(date)
      }
    }
  }

  /**
   * Navigate to the previous month
   */
  const prevMonth = () => {
    // Clone to avoid mutating the original
    const newMonth = currentMonth.clone().subtract(1, 'month')

    // Get the beginning of the earliest month available
    const earliestMonth = dateConstraints.minDate.clone().startOf('month')

    // If trying to navigate to a month before the earliest available month,
    // jump directly to the earliest month instead
    if (newMonth.isBefore(earliestMonth, 'month')) {
      setCurrentMonth(earliestMonth)
      setSelectedMonth(capitalizeFirstLetter(earliestMonth.locale(language).format('MMMM YYYY')))
      return
    }

    setCurrentMonth(newMonth)
    setSelectedMonth(capitalizeFirstLetter(newMonth.locale(language).format('MMMM YYYY')))
  }

  /**
   * Navigate to the next month
   */
  const nextMonth = () => {
    // Clone to avoid mutating the original
    const newMonth = currentMonth.clone().add(1, 'month')
    // Prevent navigating beyond current month
    if (newMonth.isAfter(dayjs().startOf('month'), 'month')) {
      return
    }
    setCurrentMonth(newMonth)
    setSelectedMonth(capitalizeFirstLetter(newMonth.locale(language).format('MMMM YYYY')))
  }

  /**
   * Handle month selection from dropdown
   * @param {Object} event - The select event
   */
  const handleMonthChange = (event) => {
    const selectedMonthStr = event.target.value
    setSelectedMonth(selectedMonthStr)

    // Parse the month and year from the selected string
    // Format is expected to be "Month YYYY" based on locale
    const parts = selectedMonthStr.split(' ')
    const yearStr = parts[parts.length - 1] // Last part is always the year
    const year = parseInt(yearStr, 10)

    // Get localized month names based on current language
    const monthNames = dayjs().locale(language).localeData().months()

    // Find the month by trying to match the localized month name in the string
    const monthPart = selectedMonthStr.replace(yearStr, '').trim()

    // Try to find the month in the localized month names list
    let monthIndex = -1
    for (let i = 0; i < monthNames.length; i++) {
      if (monthPart.toLowerCase() === monthNames[i].toLowerCase()) {
        monthIndex = i
        break
      }
    }

    // If not found, try a more flexible match (for abbreviated month names)
    if (monthIndex === -1) {
      for (let i = 0; i < monthNames.length; i++) {
        if (monthPart.toLowerCase().startsWith(monthNames[i].toLowerCase().substr(0, 3))) {
          monthIndex = i
          break
        }
      }
    }

    // Create a new dayjs object with the selected month and year
    const month = dayjs().locale(language).month(monthIndex !== -1 ? monthIndex : 0).year(year)
    setCurrentMonth(month)
  }

  /**
   * Generate available months for dropdown
   * @returns {Array} List of available month strings
   */
  const getAvailableMonths = () => {
    const months = []
    let current = dateConstraints.minDate.clone().startOf('month')
    const end = dayjs().startOf('month')

    while (current.isSameOrBefore(end, 'month')) {
      // Use dayjs formatting with the current locale for month names
      // Force locale to match the currently selected language
      const monthYearString = current.locale(language).format('MMMM YYYY')
      months.push(capitalizeFirstLetter(monthYearString))
      current = current.add(1, 'month')
    }

    return months
  }

  /**
   * Apply the selected date range as a filter
   */
  const applyDateRange = () => {
    if (!startDate || !endDate) return

    // Remove old date range filter if exists
    const dateRangeFilter = selectedFilters.find(filter => filter.startsWith('dateRange:'))
    if (dateRangeFilter) {
      deleteSelectedFilter(dateRangeFilter)
    }

    // Format start date with beginning of day (00:00:00)
    // Use ISO format for consistency across locales in filter values
    const formattedStartDate = startDate.startOf('day').format('YYYY-MM-DD')

    // Format end date with end of day (23:59:59)
    // Use ISO format for consistency across locales in filter values
    const formattedEndDate = endDate.endOf('day').format('YYYY-MM-DD')

    // Add new filter with date range information
    const value = `${formattedStartDate} - ${formattedEndDate}`
    const newDateRange = `dateRange: ${value}`
    addSelectedFilter(newDateRange)

    return true
  }

  /**
   * Clear the selected date range
   */
  const clearDateRange = () => {
    const dateRangeFilter = selectedFilters.find(filter => filter.startsWith('dateRange:'))
    if (dateRangeFilter) {
      deleteSelectedFilter(dateRangeFilter)
    }
    setStartDate(null)
    setEndDate(null)
  }

  /**
   * Format the selected date range for display
   * @returns {string} Formatted date range or default label
   */
  const formatDateRange = (t) => {
    if (startDate && endDate) {
      const today = dayjs().startOf('day')
      const yesterday = today.subtract(1, 'day')
      const isToday = day => day.isSame(today, 'day')
      const isYesterday = day => day.isSame(yesterday, 'day')

      // Check for common patterns like "Today", "Yesterday", etc.
      if (isToday(startDate) && isToday(endDate)) {
        return t('app:filters.dateRange.today', { defaultValue: 'Today' })
      }

      if (isYesterday(startDate) && isYesterday(endDate)) {
        return t('app:filters.dateRange.yesterday', { defaultValue: 'Yesterday' })
      }

      // Get locale-specific format patterns
      const formats = getLocaleFormats(language)

      // Use locale-aware formatting for other date ranges
      if (startDate.year() === endDate.year() && startDate.month() === endDate.month()) {
        // Same month and year: Use locale-specific format
        // French: "Jan 1-15, 2025" vs English: "Jan 1-15, 2025" (same in this case)
        return `${capitalizeFirstLetter(startDate.format(formats.month))} ${startDate.date()}-${endDate.date()}, ${endDate.year()}`
      } else if (startDate.year() === endDate.year()) {
        // Same year, different month: Use locale-specific month-day format
        // French: "1 Jan - 15 Fév, 2025" vs English: "Jan 1 - Feb 15, 2025"
        return `${startDate.format(formats.monthDay)} - ${endDate.format(formats.monthDay)}, ${endDate.year()}`
      } else {
        // Different years: Use dayjs localized long format which respects locale
        // This will automatically format according to the locale set via dayjs.locale()
        return `${startDate.format(formats.longDate)} - ${endDate.format(formats.longDate)}`
      }
    } else {
      return t('app:filters.dateRange.title', { defaultValue: 'DATE RANGE' })
    }
  }

  // Check if previous/next month navigation should be disabled
  const isPrevMonthDisabled = currentMonth.clone().startOf('month').isSame(
    dateConstraints.minDate.clone().startOf('month'), 'month',
  )

  const isNextMonthDisabled = currentMonth.clone().add(1, 'month').startOf('month').isAfter(dayjs().startOf('month'))

  /**
   * Check if a date range filter exists in the selected filters
   * @returns {boolean} True if a date range filter exists
   */
  const hasDateRangeFilter = () => {
    return selectedFilters.some(filter => filter.startsWith('dateRange:'))
  }

  /**
   * Get locale-specific date format patterns (wrapper for utility function)
   * @param {string} lang - The language code
   * @returns {object} Object containing format patterns for the language
   */
  const getLocaleFormatsForLanguage = (lang) => {
    return getLocaleFormats(lang)
  }

  return {
    // State
    startDate,
    endDate,
    currentMonth,
    selectedMonth,
    dateConstraints,
    userMembership,
    selectedCamera,
    isPrevMonthDisabled,
    isNextMonthDisabled,
    selectedFilters,
    language,

    // Methods
    setStartDate,
    setEndDate,
    setCurrentMonth,
    setSelectedMonth,
    handleDateClick,
    prevMonth,
    nextMonth,
    handleMonthChange,
    getAvailableMonths,
    applyDateRange,
    clearDateRange,
    formatDateRange,
    initializeCalendarView,
    hasDateRangeFilter,
    getLocaleFormats: getLocaleFormatsForLanguage,
  }
}

export default useSpyPointDateRange
